// Auth token store backed by localStorage so a fresh tab can reuse an
// existing login. Expiry is enforced on load; a `storage` event syncs
// login/logout across tabs of the same origin.

const STORAGE_KEY = "auth_tokens";

function loadTokens() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.expiresAt || Date.now() >= parsed.expiresAt) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* localStorage unavailable */
    }
    return null;
  }
}

let tokens = loadTokens();
const listeners = new Set();

function notify() {
  listeners.forEach((cb) => {
    try {
      cb(tokens);
    } catch {
      /* subscriber threw — ignore so one bad listener can't break the rest */
    }
  });
}

if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.storageArea && event.storageArea !== localStorage) return;
    // event.key is null when localStorage is cleared entirely.
    if (event.key !== null && event.key !== STORAGE_KEY) return;
    tokens = loadTokens();
    notify();
  });
}

export function getAccessToken() {
  return tokens ? tokens.accessToken : null;
}

export function getIdentity() {
  return tokens ? tokens.identity : null;
}

export function setTokens({ accessToken, expiresIn, identity }) {
  const ttlMs = Math.max(0, Number(expiresIn) || 0) * 1000;
  tokens = {
    accessToken,
    identity: identity || null,
    expiresAt: Date.now() + ttlMs,
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
  } catch {
    /* localStorage unavailable — session-only in memory */
  }
  notify();
}

export function clearTokens() {
  tokens = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* localStorage unavailable */
  }
  notify();
}

export function subscribeToAuthChanges(callback) {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}
